/*
 * Copyright (C) 2018 BarD Software s.r.o
 */
type HttpMethod = 'GET' | 'POST';

import $ from 'jquery';

declare global {
    interface Window {
        mxLoadResources: boolean;
        mxLoadStylesheets: boolean;
        mxForceIncludes: boolean;
        mxResourceExtension: string;
        websocketHost: string;
        needsCookieConsent: boolean;
        currentUserId: string;
    }
}

export const hostname = window.location.hostname;
const websocketHost = window.websocketHost;
const isSecureHost = hostname === "ganttproject.cloud" || hostname === "cloud.ganttproject.biz" || hostname === "localhost";

export class Endpoint<T> {
    constructor(readonly url: string,
                private readonly method: HttpMethod = 'GET',
                private readonly host: string = hostname,
                private readonly port: number = 80,
                private readonly processData: boolean = true,
                private readonly isSecure: boolean = isSecureHost,
                private readonly jsonBody: boolean = false) {}

    public invoke(data: T): JQueryXHR {
        const scheme = this.isSecure ? 'https' : 'http';
        const ajaxSettings: any = {
            url: `${scheme}://${this.host}${this.port === 80 ? '' : ':' + this.port}${this.url}`,
            method: this.method,
            data,
        };

        if (this.jsonBody) {
            ajaxSettings.contentType = 'application/json';
            ajaxSettings.data = JSON.stringify(data);
        } else if (!this.processData) {
            const formData = new FormData();
            for (const key in data) {
                if (data.hasOwnProperty(key)) {
                    formData.append(key, data[key] as any);
                }
            }

            ajaxSettings.cache = false;
            ajaxSettings.processData = false;
            ajaxSettings.contentType = false;
            ajaxSettings.data = formData;
        }

        return $.ajax(ajaxSettings);
    }

    public redirect(data?: T) {
        console.trace('Redirecting to ' + this.absoluteUrl(data));
        window.location.assign(this.absoluteUrl(data));
    }

    public absoluteUrl(data?: T): string {
        if (data) {
            const queryString = $.param(data);
            return `${this.isSecure ? 'https' : 'http'}://${this.host}${this.url}?${queryString}`;
        } else {
            return `${this.isSecure ? 'https' : 'http'}://${this.host}${this.url}`;
        }
    }

    public relativeUrl(data?: T): string {
        if (data) {
            const queryString = $.param(data);
            return `${this.url}?${queryString}`;
        } else {
            return `${this.url}`;
        }
    }
}

export class EndpointBuilder<T> {
    public method: HttpMethod = 'GET';
    public host: string = hostname;
    public port: number = 80;
    public processData: boolean = true;
    public isSecure: boolean = isSecureHost;
    public jsonBody: boolean = false;

    constructor(private readonly url: string) {}

    public build(): Endpoint<T> {
        return new Endpoint<T>(
          this.url, this.method, this.host, this.port, this.processData, this.isSecure, this.jsonBody,
        );
    }
}

export interface TeamCreateArgs {
    name: string;
    description: string;
}
export const TeamCreate = new Endpoint<TeamCreateArgs>('/team/create', 'POST');

export interface TeamExpelArgs {
    teamRefid: string;
    userId?: string;
}
export const TeamExpel = new Endpoint<TeamExpelArgs>('/team/expel', 'POST');

export interface TeamGetArgs {
    refid: string;
}
export const TeamGet = new Endpoint<TeamGetArgs>('/team/get', 'GET');

// --------------- /team/invite ---------------
export interface TeamInviteArgs {
    refid: string;
    name: string;
    email: string;
}
export const TeamInvite = new Endpoint<TeamInviteArgs>('/team/invite', 'POST');

// --------------- /team/list ---------------
export interface TeamListArgs {
    owned: boolean;
    participated: boolean;
}
export const TeamList = new Endpoint<TeamListArgs>('/team/list', 'GET');

// --------------- /team/remove ---------------
export interface TeamRemoveArgs {
    teamRefid: string;
}
export const TeamRemove = new Endpoint<TeamRemoveArgs>('/team/remove', 'POST');

// --------------- /team/update ---------------
export interface TeamUpdateArgs {
    teamRefid: string;
    name: string;
    description: string;
    billingRefid: string;
}
export const TeamUpdate = new Endpoint<TeamUpdateArgs>('/team/update', 'POST');

// --------------- /team/resources/list -------
export interface TeamResourcesListArgs {
    teamRefid: string;
}
export const TeamResourcesList = new Endpoint<TeamResourcesListArgs>('/team/resources/list', 'GET');

// ------------- /resource/assignments/list ---
export interface ResourceAssignmentsListArgs {
    teamRefid: string;
    email: string;
}
export const ResourceAssignmentsList = new Endpoint<ResourceAssignmentsListArgs>('/resource/assignments/list', 'GET');

// -------------- /team/resources/loads/graph --
export interface TeamResourcesLoadsGraphArgs {
    teamRefid: string;
    emails: string[];
    projectIds: string[];
}
const loadsGraphBuilder = new EndpointBuilder<TeamResourcesLoadsGraphArgs>('/team/resources/loads/graph');
loadsGraphBuilder.jsonBody = true;
loadsGraphBuilder.method = 'POST';
export const TeamResourcesLoadsGraph = loadsGraphBuilder.build();

// --------------- /team/candidates/list -------
export interface TeamCandidatesListArgs {
    teamRefid: string;
}
export const TeamCandidatesList = new Endpoint<TeamCandidatesListArgs>('/team/candidates/list', 'GET');

// --------------- /user/create ---------------
export interface UserCreateArgs {
    id: string;
    email: string;
    displayName: string;
    promo?: string;
    invite?: string;
}
export const UserCreate = new Endpoint<UserCreateArgs>('/user/create', 'POST');

// --------------- /user/get ---------------
export interface UserGetArgs {
    id: string;
    idToken: string;
}
export const UserGet = new Endpoint<UserGetArgs>('/user/get', 'POST');

// --------------- /user/update ---------------
export interface UserUpdateArgs {
    displayedName: string;
    emailConsents: number;
}
export const UserUpdate = new Endpoint<UserUpdateArgs>('/user/update', 'POST');

export const DownloadStuff = new Endpoint<undefined>('/user/export', 'GET');
export const UserDelete = new Endpoint<undefined>('/user/delete', 'POST');

export const Dashboard = new Endpoint<void>('/me', 'GET');
export const Landing = new Endpoint<void>('/', 'GET');
export const MeBillingList = new Endpoint<undefined>('/api/billing', 'GET');
export const PostAuthPage = new Endpoint<void>('/post-auth', 'GET');

export interface BillingCreateArgs {
    name: string;
    description: string;
}
export const MeBillingCreate = new Endpoint<BillingCreateArgs>('/api/billing/create', 'POST');

export interface BillingRecordsArgs {
    refid: string
}
export const BillingRecords = new Endpoint<BillingRecordsArgs>('/api/billing/records', 'GET');

export interface BillingUpdateArgs {
    refid: string;
    name: string;
    description: string;
}
export const MeBillingUpdate = new Endpoint<BillingUpdateArgs>('/api/billing/update', 'POST');


export interface BillingTopUpPaddleArgs {
    txnId: string;
    billingId: string;
}

export interface BillingTopUpPaddleResponse {
    status: number;
    statusMessage: string;
}

export const MeBillingTopUpPaddle = new Endpoint<BillingTopUpPaddleArgs>("/api/billing/topup/paddle", "POST");

// --------------- /api/billing/promo ---------------
export interface BillingApplyPromoArgs {
    code: string;
    billingRefid: string;
}
export interface BillingApplyPromoResponse {
    credit: number;
}
export const BillingApplyPromo = new Endpoint<BillingApplyPromoArgs>('/api/billing/promo', 'POST');

export const MeInvites = new Endpoint<undefined>('/api/invites', 'GET');
/** @param from the page that sent the user here: only /signin tells a stranger apart from a sign-up
 * that stopped halfway, so post-auth cannot work it out on its own. */
export interface PostAuthArgs {
    from?: 'signin';
}
export const PostAuth = new Endpoint<PostAuthArgs>('/post-auth', 'GET');

export interface SigninArgs {
    redirect?: string;
    callback?: number;
    fallback?: string;
}
export const Signin = new Endpoint<SigninArgs>('/signin', 'GET');
export const Signup = new Endpoint<SigninArgs>('/signup', 'GET');
export const SetRedirect = new Endpoint<SigninArgs>('/__/auth/set-redirect', 'POST');
export interface SetIdTokenArgs {
    user: string;
}
export const SetIdToken = new Endpoint<SetIdTokenArgs>('/__/auth/set-id-token', 'POST');
export const ClearRedirect = new Endpoint<void>('/__/auth/clear-redirect', 'POST');
/** Tells the backend to invalidate the session when the user signs out. */
export const SignOut = new Endpoint<undefined>('/__/auth/sign-out', 'POST');

export interface DesktopSigninArgs {
    callback: number;
    fallback?: string;
}
export const DesktopSignin = new Endpoint<DesktopSigninArgs>('/__/auth/desktop', 'GET');

export interface AccessTokenGetArgs {
    validUntil: number;
}
export interface AccessTokenGetDto {
    token: string;
    validity: number;
    userId: string;
}

export const AccessTokenGet = new Endpoint<AccessTokenGetArgs>('/access-token/get', 'POST');

export interface TeamJoinArgs {
    code: string;
    action: string;
}
export const TeamJoin = new Endpoint<TeamJoinArgs>('/join', 'GET');

// --------------- /api/invite/info ---------------
export interface JoinInfoArgs {
    code: string;
}
export const JoinInfo = new Endpoint<JoinInfoArgs>('/api/invite/info', 'GET');

// --------------- /api/invite/accept ---------------
export interface JoinAcceptArgs {
    code: string;
}
export const JoinAccept = new Endpoint<JoinAcceptArgs>('/api/invite/accept', 'POST');

export interface CancelInviteArgs {
    code: string;
}
export const CancelInvite = new Endpoint<CancelInviteArgs>('/team/invite/cancel', 'POST');

export const AboutBeta = new Endpoint<void>('/about/private-beta', 'GET');
export const AboutTos = new Endpoint<void>('/about/tos', 'GET');
export const AboutPrivacy = new Endpoint<void>('/about/privacy', 'GET');
export const AboutPricing = new Endpoint<void>('/about/pricing', 'GET');
export const AboutTeam = new Endpoint<void>('/about/team', 'GET');

export const tradewinds = 'wss://' + websocketHost;
